import React, { useEffect, useMemo, useState } from "react";

import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Spinner,
    TabContent,
    TabPane,
    UncontrolledDropdown,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { APIClient, setAuthorization } from "../../helpers/api_helper";
import ToastAlert from "../../Components/Common/ToastAlert";
import CustomTableContainer from "../../Components/Common/CustomTableContainer";
import AddLocationModal from "./QuickAddInput";
import AddKeywordModal from "./AddKeywordModal";
import QuickEditInput from "./QuickEditInput";
import NewComboModal from "./NewComboModal";

const Index = () => {
    const api = useMemo(() => new APIClient(), []);
    setAuthorization(localStorage.getItem("token"));

    const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
    const [isAddKeywordOpen, setIsAddKeywordOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isNewComboOpen, setIsNewComboOpen] = useState(false);
    const [inputId, setInputId] = useState(null);

    const [inputList, setInputList] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10000);
    const [pageNumber, setPageNumber] = useState(1);
    const [loadingState, setLoadingState] = useState(false);
    const [maxPage, setMaxPage] = useState(1);

    const distinctLocations = useMemo(() => {
        const map = new Map();
        inputList.forEach(i => {
            const key = `${i.location}|${i.country}`;
            if (!map.has(key)) map.set(key, { location: i.location, country: i.country, count: 0 });
            map.get(key).count++;
        });
        return [...map.values()];
    }, [inputList]);

    const distinctKeywords = useMemo(() => {
        const map = new Map();
        inputList.forEach(i => map.set(i.keyword, (map.get(i.keyword) || 0) + 1));
        return [...map.entries()].map(([keyword, count]) => ({ keyword, count }));
    }, [inputList]);

    const [columns] = useState([
        { Header: "Location", accessor: "location" },
        { Header: "Keyword", accessor: "keyword" },
        { Header: "Country", accessor: "country" },
        {
            Header: "Total Jobs",
            accessor: "job_count",
            Cell: (cellProps) => (
                <span className="fw-medium">{cellProps.row.original.job_count || 0}</span>
            ),
        },
        {
            Header: "Open Jobs",
            accessor: "open_job_count",
            Cell: (cellProps) => (
                <span className="fw-medium">{cellProps.row.original.open_job_count || 0}</span>
            ),
        },
        {
            Header: "Total Companies",
            accessor: "company_count",
            Cell: (cellProps) => (
                <span className="fw-medium">{cellProps.row.original.company_count || 0}</span>
            ),
        },
        {
            Header: "Avg. Closing Time",
            accessor: "avg_closing_days",
            Cell: (cellProps) => {
                const avgDays = cellProps.row.original.avg_closing_days;
                if (avgDays === null || avgDays === undefined) return "-";
                return `${Number(avgDays).toFixed(1)} days`;
            },
        },
        {
            Header: "Actions",
            Cell: (cellProps) => (
                <UncontrolledDropdown>
                    <DropdownToggle href="#" className="btn-soft-secondary btn-sm" tag="button">
                        <i className="ri-more-fill" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                        <DropdownItem onClick={() => editInput(cellProps.row.original.id)}>
                            <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                            Edit
                        </DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem onClick={() => deleteInput(cellProps.row.original.id)}>
                            <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                            Delete
                        </DropdownItem>
                    </DropdownMenu>
                </UncontrolledDropdown>
            ),
            sortable: false,
        },
    ]);

    const toggleAddLocation = () => setIsAddLocationOpen(v => !v);
    const toggleAddKeyword = () => setIsAddKeywordOpen(v => !v);
    const toggleEditModal = () => setIsEditModalOpen(v => !v);
    const toggleNewCombo = () => setIsNewComboOpen(v => !v);

    const editInput = (id) => {
        setInputId(id);
        toggleEditModal();
    };

    const deleteInput = async (id) => {
        try {
            await api.delete(`v1/inputs/${id}`);
            await getInputList();
            ToastAlert("Record deleted successfully.", "success");
        } catch (error) {
            ToastAlert(error, "error");
        }
    };

    const deleteLocation = async (location, country) => {
        const rows = inputList.filter(i => i.location === location && i.country === country);
        try {
            await Promise.all(rows.map(i => api.delete(`v1/inputs/${i.id}`)));
            await getInputList();
            ToastAlert(`${rows.length} records deleted.`, "success");
        } catch (error) {
            ToastAlert(error, "error");
        }
    };

    const deleteKeyword = async (keyword) => {
        const rows = inputList.filter(i => i.keyword === keyword);
        try {
            await Promise.all(rows.map(i => api.delete(`v1/inputs/${i.id}`)));
            await getInputList();
            ToastAlert(`${rows.length} records deleted.`, "success");
        } catch (error) {
            ToastAlert(error, "error");
        }
    };

    const getInputList = async () => {
        setLoadingState(true);
        try {
            const url = `v1/inputs/with-stats?page=${pageNumber}&page_size=10000`;
            const response = await api.get(url);
            if (response && response.data && Array.isArray(response.data)) {
                setInputList(response.data);
                setRowCount(response.count || 0);
                setMaxPage(response.max_page || 1);
            } else if (Array.isArray(response)) {
                setInputList(response);
                setRowCount(response.length || 0);
                setMaxPage(Math.ceil((response.length || 0) / pageSize));
            } else if (response && Array.isArray(response.results)) {
                setInputList(response.results);
                setRowCount(response.count || response.results.length || 0);
                setMaxPage(Math.ceil((response.count || 0) / pageSize));
            } else {
                setInputList([]);
                setRowCount(0);
                setMaxPage(1);
            }
        } catch (error) {
            ToastAlert(error, "error");
        } finally {
            setLoadingState(false);
        }
    };

    useEffect(() => {
        getInputList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber]);

    useEffect(() => {
        if (pageNumber > 1) {
            setPageNumber(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    document.title = "Settings · Horus";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Talent searches" pageTitle="Settings" hidden />

                    {/* Panels row */}
                    <Row className="mb-3">
                        {/* Locations panel */}
                        <Col md={6}>
                            <Card className="h-100">
                                <CardHeader className="border-bottom-dashed d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-semibold">Locations</h6>
                                    <Button size="sm" color="success" onClick={toggleAddLocation}>
                                        <i className="ri-add-line align-bottom me-1"></i>
                                        Add Location
                                    </Button>
                                </CardHeader>
                                <CardBody style={{ maxHeight: "260px", overflowY: "auto" }}>
                                    {distinctLocations.length === 0 ? (
                                        <p className="text-muted small mb-0">No locations yet.</p>
                                    ) : (
                                        distinctLocations.map(loc => (
                                            <div
                                                key={`${loc.location}|${loc.country}`}
                                                className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                            >
                                                <span>
                                                    {loc.location}{" "}
                                                    <span className="text-muted">({loc.country})</span>
                                                </span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Badge color="primary" pill>{loc.count} kw</Badge>
                                                    <button
                                                        className="btn btn-sm btn-soft-danger py-0 px-1"
                                                        title="Konumu sil"
                                                        onClick={() => deleteLocation(loc.location, loc.country)}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Keywords panel */}
                        <Col md={6}>
                            <Card className="h-100">
                                <CardHeader className="border-bottom-dashed d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-semibold">Keywords</h6>
                                    <Button size="sm" color="primary" onClick={toggleAddKeyword}>
                                        <i className="ri-add-line align-bottom me-1"></i>
                                        Add Keyword
                                    </Button>
                                </CardHeader>
                                <CardBody style={{ maxHeight: "260px", overflowY: "auto" }}>
                                    {distinctKeywords.length === 0 ? (
                                        <p className="text-muted small mb-0">No keywords yet.</p>
                                    ) : (
                                        distinctKeywords.map(kw => (
                                            <div
                                                key={kw.keyword}
                                                className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                            >
                                                <span>{kw.keyword}</span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Badge color="success" pill>{kw.count} loc</Badge>
                                                    <button
                                                        className="btn btn-sm btn-soft-danger py-0 px-1"
                                                        title="Kelimeyi sil"
                                                        onClick={() => deleteKeyword(kw.keyword)}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Combinations table */}
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <CardHeader className="border-bottom-dashed d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-semibold">All Combinations</h6>
                                    <Button size="sm" color="warning" onClick={toggleNewCombo}>
                                        <i className="ri-add-line align-bottom me-1"></i>
                                        New Combination
                                    </Button>
                                </CardHeader>
                                <CardBody>
                                    <TabContent className="text-muted">
                                        <TabPane>
                                            <div id="table-input-list-all" className="table-card gridjs-border-none pb-2">
                                                {!loadingState ? (
                                                    inputList && inputList.length > 0 ? (
                                                        <CustomTableContainer
                                                            columns={columns}
                                                            data={inputList}
                                                            rowCount={rowCount}
                                                            maxPage={maxPage}
                                                            handlePageChange={setPageNumber}
                                                            handlePageSize={setPageSize}
                                                            currentPageSize={pageSize}
                                                            currentPage={pageNumber}
                                                            divClass="table-responsive mb-1"
                                                            divStyle={{ overflow: "visible" }}
                                                            tableClass="mb-0 align-middle table-borderless"
                                                            theadClass="table-light text-muted"
                                                        />
                                                    ) : (
                                                        <div className="py-4 text-center">
                                                            <div>
                                                                <lord-icon
                                                                    src="https://cdn.lordicon.com/msoeawqm.json"
                                                                    trigger="loop"
                                                                    colors="primary:#405189,secondary:#0ab39c"
                                                                    style={{ width: "72px", height: "72px" }}
                                                                ></lord-icon>
                                                            </div>
                                                            <div className="mt-4">
                                                                <h5>No records yet. Start by adding a location or keyword.</h5>
                                                            </div>
                                                        </div>
                                                    )
                                                ) : (
                                                    <div className="py-4 text-center">
                                                        <Spinner color="primary" />
                                                    </div>
                                                )}
                                            </div>
                                        </TabPane>
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {isAddLocationOpen && (
                <AddLocationModal
                    isOpen={isAddLocationOpen}
                    toggle={toggleAddLocation}
                    existingKeywords={distinctKeywords}
                    getInputList={getInputList}
                />
            )}

            {isAddKeywordOpen && (
                <AddKeywordModal
                    isOpen={isAddKeywordOpen}
                    toggle={toggleAddKeyword}
                    existingLocations={distinctLocations}
                    getInputList={getInputList}
                />
            )}

            {isEditModalOpen && (
                <QuickEditInput
                    isEditModalOpen={isEditModalOpen}
                    toggleEditModal={toggleEditModal}
                    inputId={inputId}
                    getInputList={getInputList}
                />
            )}

            {isNewComboOpen && (
                <NewComboModal
                    isOpen={isNewComboOpen}
                    toggle={toggleNewCombo}
                    getInputList={getInputList}
                />
            )}
        </React.Fragment>
    );
};

export default Index;
